import { deserializeGameState } from '@shared/protocol/serialize';
import type { ServerMessage } from '@shared/protocol';
import type { ScreenHandle, ScreenMountContext } from './types';
import type { BoardMountPayload } from './board-payload';
import { createButton } from '../components/button';
import type { UIComponentHandle } from '../components/types';
import { buildInventoryItemMeta } from './item-ui-meta';
import { itemIconUrl } from './item-assets';
import { createBoardTopTimer } from './board-timer';

const Z_INDEX_CACHE_MODAL = '10050';
const PANEL_BG = 'rgba(30, 35, 50, 0.96)';
const BODY_FAMILY = "'Nunito Sans', 'Nunito', 'Arial Rounded MT Bold', sans-serif";
const HEADLINE_FAMILY = "'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif";

export function createWeaponsCacheScreen(ctx: ScreenMountContext<BoardMountPayload>): ScreenHandle {
  const { gsapRegistry, host, router, payload } = ctx;
  const previousHostZIndex = host.style.zIndex;
  const pending = payload.state.pendingBoardEncounter;
  const itemOptions = pending?.kind === 'weapons_cache' ? (pending.itemOptions ?? []) : [];
  const itemMeta = buildInventoryItemMeta('board');

  let disposed = false;
  let latestState = payload.state;
  let choiceMade = false;
  let chosenItemId: string | null = null;
  let actionControlsLocked = payload.isActionControlLocked?.() ?? false;
  let closeTraceSent = false;
  let unsubscribe: (() => void) | null = null;
  let unsubscribeActionLock: (() => void) | null = null;
  const buttons: UIComponentHandle[] = [];

  host.style.zIndex = Z_INDEX_CACHE_MODAL;

  const root = document.createElement('div');
  root.className = 'screen-weapons-cache';
  root.setAttribute('data-screen-id', 'weapons-cache');
  root.style.cssText = [
    'position: fixed',
    'inset: 0',
    `z-index: ${Z_INDEX_CACHE_MODAL}`,
    'display: flex',
    'align-items: center',
    'justify-content: center',
    'background: rgba(0,0,0,0.32)',
    'pointer-events: auto',
  ].join('; ');

  const boardTimer = createBoardTopTimer(payload, { subscribe: false });
  root.appendChild(boardTimer.element);

  const panel = document.createElement('div');
  panel.className = 'weapons-cache-dialog';
  panel.style.cssText = [
    'width: min(860px, calc(100vw - 32px))',
    `background: ${PANEL_BG}`,
    'border: 2px solid rgba(255,255,255,0.14)',
    'border-radius: 12px',
    'padding: 24px',
    'box-shadow: 0 8px 24px rgba(0,0,0,0.60)',
    'box-sizing: border-box',
  ].join('; ');

  const title = document.createElement('div');
  title.textContent = 'Weapons Cache';
  title.style.cssText = [
    `font-family: ${HEADLINE_FAMILY}`,
    'font-size: 32px',
    'line-height: 1',
    'color: #fff',
    'text-shadow: 0 3px 0 #000',
    'margin-bottom: 8px',
  ].join('; ');

  const subtitle = document.createElement('div');
  subtitle.textContent = 'Choose one item';
  subtitle.style.cssText = [
    `font-family: ${BODY_FAMILY}`,
    'font-size: 18px',
    'font-weight: 800',
    'color: rgba(255,255,255,0.82)',
    'margin-bottom: 18px',
  ].join('; ');

  const grid = document.createElement('div');
  grid.className = 'weapons-cache-grid';
  grid.style.cssText = [
    'display: grid',
    'grid-template-columns: repeat(auto-fit, minmax(180px, 1fr))',
    'gap: 12px',
  ].join('; ');

  for (const itemId of itemOptions) {
    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'min-width: 0';

    const meta = itemMeta[itemId];
    const label = meta?.name ?? itemId.replace(/_/g, ' ');
    const button = createButton({
      gsapRegistry,
      mount: wrapper,
      variant: 'secondary',
      size: 'large',
      label,
      onClick: () => claim(itemId),
    });
    const buttonEl = button.element as HTMLButtonElement;
    buttonEl.style.width = '100%';
    buttonEl.style.minHeight = '112px';
    buttonEl.style.display = 'grid';
    buttonEl.style.gridTemplateColumns = '76px minmax(0, 1fr)';
    buttonEl.style.alignItems = 'center';
    buttonEl.style.gap = '12px';
    buttonEl.style.textAlign = 'left';
    buttonEl.style.whiteSpace = 'normal';

    const icon = document.createElement('img');
    icon.src = itemIconUrl(itemId);
    icon.alt = '';
    icon.style.cssText = [
      'width: 76px',
      'height: 76px',
      'object-fit: contain',
      'transform: scale(1.18)',
      'filter: drop-shadow(0 2px 2px rgba(0,0,0,0.55))',
    ].join('; ');
    buttonEl.prepend(icon);

    buttons.push(button);
    grid.appendChild(wrapper);
  }

  panel.appendChild(title);
  panel.appendChild(subtitle);
  panel.appendChild(grid);
  root.appendChild(panel);
  host.appendChild(root);
  payload.tracePresentation?.({
    stage: 'modal_open',
    screen: 'weapons-cache',
    encounterKind: 'weapons_cache',
    nodeId: pending?.nodeId,
  });

  unsubscribe = payload.subscribe((msg: ServerMessage) => {
    if (disposed) return;

    if (msg.type === 'event' && isCacheCompletionEvent(msg)) {
      closeToBoardHud('player_received_item');
      return;
    }

    if (msg.type === 'state_sync') {
      latestState = deserializeGameState(msg.state);
      boardTimer.updateFromGameState(latestState);
      if (isLocalCachePrompt(msg)) return;
      closeToBoardHud('state_sync');
      return;
    }

    if (msg.type === 'notification' && isCacheChoiceRejection(msg.text)) {
      console.warn(`[weapons-cache] server rejected cache choice: ${msg.text}`);
      choiceMade = false;
      chosenItemId = null;
      setPending(false);
    }
  });
  unsubscribeActionLock =
    payload.subscribeActionControlLock?.((locked) => {
      if (disposed || actionControlsLocked === locked) return;
      actionControlsLocked = locked;
      setPending(choiceMade);
    }) ?? null;
  setPending(choiceMade);

  function closeToBoardHud(reason: string): void {
    traceModalClose(reason);
    const nextPayload: BoardMountPayload = {
      ...payload,
      state: latestState,
    };
    dispose();
    router.navigate('board-hud', nextPayload);
  }

  function traceModalClose(reason: string): void {
    if (closeTraceSent) return;
    closeTraceSent = true;
    payload.tracePresentation?.({
      stage: 'modal_close',
      screen: 'weapons-cache',
      encounterKind: 'weapons_cache',
      nodeId: latestState.pendingBoardEncounter?.nodeId ?? pending?.nodeId,
      reason,
    });
  }

  function claim(itemId: string): void {
    if (disposed || choiceMade || actionControlsLocked) return;
    choiceMade = true;
    chosenItemId = itemId;
    setPending(true);
    payload.send({ type: 'claim_weapons_cache', itemId });
  }

  function setPending(pendingChoice: boolean): void {
    const locked = pendingChoice || actionControlsLocked;
    root.dataset.pending = pendingChoice ? 'true' : 'false';
    root.dataset.actionLocked = locked ? 'true' : 'false';
    for (const button of buttons) {
      const buttonEl = button.element as HTMLButtonElement;
      buttonEl.disabled = locked;
      buttonEl.style.pointerEvents = locked ? 'none' : 'auto';
      buttonEl.style.opacity = locked ? '0.72' : '1';
      buttonEl.toggleAttribute('data-action-locked', locked);
    }
  }

  function isLocalCachePrompt(msg: Extract<ServerMessage, { type: 'state_sync' }>): boolean {
    const activeId = msg.state.turnOrder[msg.state.currentPlayerIndex] ?? null;
    return (
      msg.state.phase === 'chest_phase' &&
      activeId === payload.localPlayerId &&
      msg.state.pendingBoardEncounter?.kind === 'weapons_cache'
    );
  }

  function isCacheCompletionEvent(msg: Extract<ServerMessage, { type: 'event' }>): boolean {
    return (
      msg.event.type === 'player_received_item' &&
      msg.event.playerId === payload.localPlayerId &&
      msg.event.source === 'weapons_cache' &&
      (chosenItemId == null || msg.event.itemId === chosenItemId)
    );
  }

  function isCacheChoiceRejection(text: string): boolean {
    return (
      text === 'no_room' ||
      text === 'not_your_turn' ||
      text === 'not_at_weapons_cache' ||
      text === 'invalid_cache_item' ||
      text === 'cache_claim_failed' ||
      text.startsWith('wrong_phase:')
    );
  }

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    traceModalClose('dispose');
    unsubscribe?.();
    unsubscribe = null;
    unsubscribeActionLock?.();
    unsubscribeActionLock = null;
    boardTimer.dispose();
    for (const button of buttons) button.dispose();
    buttons.length = 0;
    host.style.zIndex = previousHostZIndex;
    root.remove();
  }

  return { element: root, dispose };
}
